// @ts-strict-ignore
import get from 'lodash/get';
import {
  useCallback, useContext, useEffect, useState,
} from 'react';

import { ajaxFetch } from '~/helpers/api';
import { configureObservabilitySuiteContexts } from '~/helpers/initialization';
import log from '~/helpers/logging';
import { routes } from '~/helpers/routes';
import type { UserContextValue } from '~/dashboard/context';
import { defaultUserContext, UserContext } from '~/dashboard/context';

import { overrideFlagsViaQueryParams } from './helpers';
import type { FeatureFlags } from '~/helpers/typescript';

type AuthContext = {
  alloyEnvironment: string;
  isAuthenticated: boolean;
};

export function useAuthContext({
  alloyEnvironment,
  isAuthenticated,
}: AuthContext) {
  // User context state
  const [context, setContext] = useState<Partial<UserContextValue>>(defaultUserContext);

  const getUserContext = useCallback(async () => {
    try {
      const data = await ajaxFetch(routes.v3.userContext, { camelCase: true });
      if (data) {
        overrideFlagsViaQueryParams(data.featureFlags, window.location);
        setContext(data);
        // initialize sentry
        const agentData = get(data, 'agent');
        const customerData = get(data, 'customer');
        const featureFlags = get(data, 'feature_flags');
        if (agentData && customerData) {
          configureObservabilitySuiteContexts({
            agent: agentData,
            customer: customerData,
            environment: alloyEnvironment,
            featureFlags,
          });
        }
      }
    } catch (exception) {
      // TODO: remember previous page and redirect after login
      log('error', `GET ${routes.v3.userContext} exception`, {}, exception);
    }
  }, [alloyEnvironment]);

  useEffect(() => {
    if (isAuthenticated) {
      getUserContext();
    }
  }, [getUserContext, isAuthenticated]);

  return {
    authContext: {
      alloyEnvironment,
      isAuthenticated,
    },
    getUserContext,
    setContext,
    userContext: context,
  };
}

export function useAlertColors({
  organizationName,
  isAuthenticated,
}: {
  organizationName?: string;
  isAuthenticated: boolean;
}) {
  // Alert/tag colors state
  const [alertColors, setAlertColors] = useState({});

  const getAlertColors = useCallback(async () => {
    try {
      // NOTE: passing an option to convert the response to a specific format (i.e. camelCase or
      //  snake-case) can result in meaningful characters being trimmed away. Beware!!!
      const response = await ajaxFetch(routes.v3.cases.alertColors);
      if (response?.colors) setAlertColors({ ...response.colors });
    } catch (exception) {
      log(
        'error',
        `GET ${routes.v3.cases.alertColors} exception`,
        {},
        exception,
      );
    }
  }, [setAlertColors]);

  useEffect(() => {
    if (isAuthenticated && organizationName) {
      getAlertColors();
    }
  }, [getAlertColors, isAuthenticated, organizationName]);

  return { alertColors };
}

export function useExpandingSidebar() {
  // Dashboard sidebar expanded/collapsed state — read synchronously so the sidebar
  // renders in its correct state on mount and avoids a width transition on every load.
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    const stored = localStorage.getItem('sidebarExpanded');
    return stored ? JSON.parse(stored) : false;
  });

  const toggleSidebarExpanded = useCallback(() => {
    setSidebarExpanded(expanded => {
      window.localStorage.setItem(
        'sidebarExpanded',
        expanded ? 'false' : 'true',
      );

      return !expanded;
    });
  }, []);

  // Whether the sidebar's mobile drawer is open on small screens. Not persisted — unlike
  // `sidebarExpanded`, FE2.0's equivalent mobile-open state doesn't persist across reloads either.
  const [isOpenMobile, setIsOpenMobile] = useState(false);

  const onOpenMobileChange = useCallback((open: boolean) => {
    setIsOpenMobile(open);
  }, []);

  return {
    sidebarExpanded, toggleSidebarExpanded, isOpenMobile, onOpenMobileChange,
  };
}

type MappedFlagValues<F extends keyof FeatureFlags> = {
  [K in F]: FeatureFlags[K];
};
/**
 * Grabs feature flags from the UserContext. Supports single and multiple, variadic arguments
 * @param flag variadic string arg specifying the flag names the get
 *
 * @example
 * // single flag
 * const isFeatureAEnabled = useFeatureFlags('flagA');
 *
 * @example
 * // multiple flags
 * const { flagA, flagB, flagC } = useFeatureFlags('flagA', 'flagB', 'flagC');
 *
 */
export function useFeatureFlags<F extends keyof FeatureFlags>(
  flag: F,
): FeatureFlags[F];
export function useFeatureFlags<F extends keyof FeatureFlags>(
  ...flags: F[]
): MappedFlagValues<F>;
export function useFeatureFlags<F extends keyof FeatureFlags>(
  ...flags: F[] | [F]
): FeatureFlags[F] | MappedFlagValues<F> {
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error(
      'useFeatureFlags() can only be used inside of a UserContext provider',
    );
  }

  if (flags.length === 1) {
    const flagName = flags[0];
    return (userContext.featureFlags?.[flagName]
      ?? userContext.feature_flags?.[flagName]) as FeatureFlags[F];
  }

  return flags.reduce((result, flagName) => {
    result[flagName] = (userContext.featureFlags[flagName]
      ?? userContext.feature_flags?.[flagName]) as FeatureFlags[F];
    return result;
  }, {} as MappedFlagValues<F>);
}
